import React, {
    ChangeEvent,
    ChangeEventHandler,
    forwardRef,
    ReactNode,
    useId,
} from 'react';
import './fm-radio-button.scss';
import {
    FMRadioButtonGroupContextValues,
    useFMRadioButtonGroupContext,
} from '../fm-radio-button-context-group';

export type THEME = 'light' | 'dark';
export type CheckedType = string | number | boolean;

interface FMRadioButtonProps
    extends Omit<
        React.ComponentPropsWithRef<'input'>,
        'type' | 'checked' | 'value'
    > {
    value: string | number;
    type?: 'radio';
    wrapperClassName?: string;
    labelClassName?: string;
    label: ReactNode;
    checked?: CheckedType;
    hideLabel?: boolean;
    theme?: THEME;
    error?: boolean;
}

interface UseInputAttributesProps {
    id?: string;
    disabled?: boolean;
    fmRadioButtonContext: FMRadioButtonGroupContextValues | null;
    checked?: CheckedType;
    value: string | number;
    name?: string;
    required?: boolean;
    error?: boolean;
    theme: THEME;
    onChange?: ChangeEventHandler<HTMLInputElement>;
}

const callAllChanges = (
    ...fun: ChangeEventHandler<HTMLInputElement>[] | any[]
) => (e: ChangeEvent<HTMLInputElement>) => {
    fun?.forEach(fn => fn?.(e));
};

function getCheckedValue(checked?: CheckedType, value?: string | number) {
    if (typeof checked === 'boolean') {
        return checked;
    } else if (['string', 'number'].includes(typeof checked)) {
        return checked == value;
    }
    return undefined;
}

function useInputAttributes({
    id: radioButtonInputId,
    disabled: radioButtonDisabled,
    fmRadioButtonContext,
    checked: radioButtonCheckedValue,
    value,
    name: radioButtonGroupName,
    required: radioButtonRequired,
    error,
    theme,
    onChange: handleOnChange,
}: UseInputAttributesProps) {
    const radioInputId = useId();
    const id = radioButtonInputId || `radio-button-${radioInputId}`;
    const disabled = fmRadioButtonContext?.disabled ?? radioButtonDisabled;
    const checkedValue = getCheckedValue(radioButtonCheckedValue, value);
    const contextCheckedValue = getCheckedValue(
        fmRadioButtonContext?.checked,
        value
    );
    const checked = contextCheckedValue ?? checkedValue;
    const name = fmRadioButtonContext?.name ?? radioButtonGroupName;
    const required = fmRadioButtonContext?.required ?? radioButtonRequired;
    const isError = fmRadioButtonContext?.error ?? error;
    const finalTheme = fmRadioButtonContext?.theme ?? theme;
    const onChange = callAllChanges(
        handleOnChange,
        fmRadioButtonContext?.onChange
    );
    return {
        id,
        disabled,
        required,
        ...(typeof value !== 'undefined' ? { value } : {}),
        ...(typeof checked !== 'undefined' ? { checked } : {}),
        ...(typeof name !== 'undefined' ? { name } : {}),
        ...(isError ? { 'data-error': 'true' } : {}),
        theme: finalTheme,
        onChange,
    };
}

const FmRadioButton = forwardRef<HTMLInputElement, FMRadioButtonProps>(
    (
        {
            id,
            type = 'radio',
            disabled,
            wrapperClassName,
            labelClassName,
            className,
            name,
            value,
            label,
            onChange,
            error,
            checked,
            required,
            hideLabel = false,
            theme = 'light',
            ...props
        },
        ref
    ) => {
        const fmRadioButtonContext = useFMRadioButtonGroupContext();
        const inputProps = useInputAttributes({
            id,
            disabled,
            theme,
            required,
            error,
            value,
            name,
            checked,
            fmRadioButtonContext,
            onChange,
        });
        return (
            <label
                className={`fm-radio-button__wrapper ${wrapperClassName ?? ''}`}
                {...(inputProps.disabled ? { 'aria-disabled': 'true' } : {})}
                data-theme={inputProps.theme}
                htmlFor={inputProps.id}
            >
                <input
                    {...props}
                    {...inputProps}
                    className={`fm-radio-button__input ${className ?? ''}`}
                    type={type || 'radio'}
                    ref={ref}
                />
                <span
                    {...(inputProps.disabled
                        ? { 'aria-disabled': 'true' }
                        : {})}
                    className={`fm-radio-button__label ${labelClassName ?? ''}`}
                    data-lablehidden={hideLabel}
                >
                    {label}
                </span>
            </label>
        );
    }
);

FmRadioButton.displayName = 'FmRadioButton';

export default FmRadioButton;
